import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper, List, ListItem, CircularProgress, Tooltip, Chip } from '@mui/material';
import { Send, Description, Lightbulb, CheckCircle, Cancel, Refresh } from '@mui/icons-material';
import { postRequest } from '../../Fetch/request';
import SpeechToTextInput from '../Common/SpeechToTextInput';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
import { NON_FUNCTIONAL_REQUIREMENT_EVALUATION_PROMPT, NFR_HINT_PROMPT } from '../../Utils/aiPrompts';
import { NON_FUNCTIONAL_REQUIREMENT_EVALUATION_SCHEMA } from '../../Utils/aiSchemas';
import './HLD.css';

export default function NonFunctionalRequirements({ 
  problemId, 
  items = [], 
  setItems,
  problemTitle = '',
  problemDescription = '',
  documentVisible = false,
  setDocumentVisible = () => {}
}) {
  const [userInput, setUserInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const chatEndRef = useRef(null);

  // Load conversation from localStorage - SIMPLE: just load and display
  // On refresh: Parent fetches backend → Updates localStorage → This component reads from localStorage
  const loadConversation = React.useCallback(() => {
    if (!problemId) return;
    
    const attemptKey = `hld-attempt-${problemId}`;
    const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Get conversation from localStorage (updated by parent on refresh)
    const savedConversation = attemptData.nonFunctionalRequirementsConversation;
    
    // Simply load the conversation if it exists
    if (savedConversation && Array.isArray(savedConversation) && savedConversation.length > 0) {
      setChatMessages(savedConversation);
    } else {
      // No conversation yet — show the opening question regardless of timestamp
      const welcomeMessage = {
        id: 'welcome-' + Date.now(),
        type: 'ai',
        content: `Now that we have the functional requirements and scale defined, let's talk about non-functional requirements for "${problemTitle || 'this system'}".\n\nWhat quality attributes does this system need to satisfy? Think about areas like availability, latency, consistency, durability, scalability, and security — go ahead and share them.`,
        timestamp: new Date().toISOString()
      };
      setChatMessages([welcomeMessage]);

      // Persist so it survives re-mounts (but don't overwrite a real conversation)
      if (!savedConversation || savedConversation.length === 0) {
        attemptData.nonFunctionalRequirementsConversation = [welcomeMessage];
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }
    
    // Remove nonFunctionalRequirements array if it exists
    if (attemptData.nonFunctionalRequirements) {
      delete attemptData.nonFunctionalRequirements;
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));
    }
  }, [problemId, problemTitle]);

  // Load conversation on mount and when problemId/problemTitle changes
  useEffect(() => {
    loadConversation();
    
    // Restore userInput from localStorage
    if (problemId) {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const savedUserInputKey = 'nonFunctionalRequirementsUserInput';
      if (attemptData[savedUserInputKey]) {
        setUserInput(attemptData[savedUserInputKey]);
      }
    }
  }, [loadConversation, problemId]);
  
  // Save userInput to localStorage whenever it changes
  useEffect(() => {
    if (problemId && userInput !== undefined) {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const savedUserInputKey = 'nonFunctionalRequirementsUserInput';
      attemptData[savedUserInputKey] = userInput;
      attemptData.timestamp = Date.now();
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));
    }
  }, [userInput, problemId]);

  // Listen for localStorage updates from parent (when backend data is loaded)
  useEffect(() => {
    if (!problemId) return;
    
    const attemptKey = `hld-attempt-${problemId}`;
    
    const handleStorageUpdate = (event) => {
      if (event.detail?.key === attemptKey) {
        loadConversation();
      }
    };
    
    window.addEventListener('localStorageUpdated', handleStorageUpdate);
    return () => window.removeEventListener('localStorageUpdated', handleStorageUpdate);
  }, [problemId, loadConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleAdd = async () => {
    if (!userInput.trim() || isSending || isValidating) return;

    const requirementText = userInput.trim();
    const newItem = {
      id: Date.now().toString(),
      text: requirementText
    };

    // Add user message to chat (no localStorage storage for conversation)
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: requirementText,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, userMessage]);

    // Validate with AI
    setIsValidating(true);
    setIsSending(true);
    
    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      // Build conversation history from chat messages (excluding welcome and hint messages)
      const conversationHistory = chatMessages
        .filter(msg => msg.type === 'user' && !msg.isHintRequest && msg.content !== 'can you give me a hint?')
        .map(msg => msg.content)
        .join('\n');
      
      // Get contextual context from localStorage
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const functionalRequirements = attemptData.functionalRequirements || [];
      const scale = attemptData.scale || [];
      const contextualContext = functionalRequirements.length > 0 
        ? `Functional Requirements: ${functionalRequirements.map(f => typeof f === 'string' ? f : f.text || f).join(', ')}`
        : '';
      
      const context = {
        problemTitle: problemTitle || 'System Design',
        userFunctionalReqs: functionalRequirements.map(f => typeof f === 'string' ? f : f.text || f),
        scale: scale.map(s => typeof s === 'string' ? s : s.text || s),
        conversationHistory: conversationHistory
      };
      
      const prompt = NON_FUNCTIONAL_REQUIREMENT_EVALUATION_PROMPT(
        context,
        requirementText,
        conversationHistory,
        contextualContext
      );
      
      const aiEvaluation = await getAIFeedbackSync(
        prompt,
        `Evaluate this non-functional requirement: ${requirementText}`,
        userId,
        'anthropic',
        null,
        NON_FUNCTIONAL_REQUIREMENT_EVALUATION_SCHEMA,
        {
          maxRetries: 3,
          onRetry: (attempt, error, delay) => {
          }
        }
      );
      
      // Parse AI response
      let parsedEvaluation = aiEvaluation;
      if (typeof aiEvaluation === 'string') {
        try {
          parsedEvaluation = JSON.parse(aiEvaluation);
        } catch (e) {
          // If not JSON, treat as plain text
          parsedEvaluation = { status: 'CORRECT', feedback: aiEvaluation };
        }
      }
      
      // Display evaluation result
      const status = parsedEvaluation.status || 'CORRECT';
      const feedback = parsedEvaluation.feedback || parsedEvaluation.evaluation_details?.reasoning || 'Validated successfully.';
      const statusIcon = status === 'CORRECT' ? '✅' : status === 'REPEATED' ? '🔄' : '❌';
      
      const aiMessage = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: `${statusIcon} **${status}**: ${feedback}\n\n${status === 'CORRECT' ? 'Requirement added successfully!' : status === 'REPEATED' ? 'This requirement seems similar to a previous one.' : 'Please review and refine this requirement.'}`,
        timestamp: new Date().toISOString(),
        evaluation: parsedEvaluation
      };
      const finalMessages = [...chatMessages, userMessage, aiMessage];
      setChatMessages(finalMessages);
      
      // Save conversation to localStorage (remove array field if it exists)
      const saveAttemptKey = `hld-attempt-${problemId}`;
      const saveAttemptData = JSON.parse(localStorage.getItem(saveAttemptKey) || '{}');
      saveAttemptData.nonFunctionalRequirementsConversation = finalMessages;
      delete saveAttemptData.nonFunctionalRequirements; // Remove array field
      saveAttemptData.timestamp = Date.now();
      localStorage.setItem(saveAttemptKey, JSON.stringify(saveAttemptData));
      
      // Save to backend fire-and-forget so loading clears immediately
      saveConversationToBackend(finalMessages);
    } catch (error) {
      const errorMessage = {
        id: `ai-error-${Date.now()}`,
        type: 'ai',
        content: `⚠️ Added: "${requirementText}"\n\nNote: AI validation failed, but requirement was added.`,
        timestamp: new Date().toISOString()
      };
      const errorMessages = [...chatMessages, userMessage, errorMessage];
      setChatMessages(errorMessages);
      
      // Save conversation to localStorage (remove array field if it exists)
      const errorAttemptKey = `hld-attempt-${problemId}`;
      const errorAttemptData = JSON.parse(localStorage.getItem(errorAttemptKey) || '{}');
      errorAttemptData.nonFunctionalRequirementsConversation = errorMessages;
      delete errorAttemptData.nonFunctionalRequirements; // Remove array field
      errorAttemptData.timestamp = Date.now();
      localStorage.setItem(errorAttemptKey, JSON.stringify(errorAttemptData));
      
      // Save to backend fire-and-forget so loading clears immediately
      saveConversationToBackend(errorMessages);
    } finally {
      setIsValidating(false);
      setIsSending(false);
      setUserInput('');
      // Clear saved userInput from localStorage after sending
      if (problemId) {
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        const savedUserInputKey = 'nonFunctionalRequirementsUserInput';
        delete attemptData[savedUserInputKey];
        attemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleHint = async () => {
    if (isSending || isValidating) return;

    setIsSending(true);
    
    // Add hint request message to chat
    const hintRequestMessage = {
      id: `hint-user-${Date.now()}`,
      type: 'user',
      content: 'can you give me a hint?',
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, hintRequestMessage]);

    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      // Build conversation history from chat messages (excluding welcome and hint messages)
      const userAttempts = chatMessages
        .filter(msg => msg.type === 'user' && !msg.isHintRequest && msg.content !== 'can you give me a hint?');
      const conversationHistory = userAttempts.map(msg => msg.content).join('\n');

      // Collect NFRs that were already accepted as CORRECT
      const alreadyCorrectNFRs = [];
      for (let i = 0; i < chatMessages.length - 1; i++) {
        const msg = chatMessages[i];
        const next = chatMessages[i + 1];
        if (msg.type === 'user' && !msg.isHintRequest && next?.type === 'ai' && next?.evaluation?.status === 'CORRECT') {
          alreadyCorrectNFRs.push(msg.content);
        }
      }

      // Get functional requirements from localStorage
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const functionalRequirements = attemptData.functionalRequirements || [];
      const functionalReqsText = functionalRequirements.length > 0
        ? functionalRequirements.map(f => typeof f === 'string' ? f : f.text || f).join(', ')
        : '';

      const hintPrompt = NFR_HINT_PROMPT(problemTitle || 'System Design', functionalReqsText, alreadyCorrectNFRs, conversationHistory);

      const aiHintResponse = await getAIFeedbackSync(
        hintPrompt,
        `Give me a hint for non-functional requirements.`,
        userId,
        'anthropic',
        null,
        null,
        {
          maxRetries: 3
        }
      );

      // Parse hint response
      let hintText = '';
      if (typeof aiHintResponse === 'string') {
        try {
          const parsed = JSON.parse(aiHintResponse);
          hintText = parsed.hint || parsed.response || aiHintResponse;
        } catch (e) {
          hintText = aiHintResponse;
        }
      } else if (aiHintResponse && aiHintResponse.hint) {
        hintText = aiHintResponse.hint;
      } else if (aiHintResponse && aiHintResponse.response) {
        hintText = aiHintResponse.response;
      } else {
        hintText = JSON.stringify(aiHintResponse);
      }

      // Add hint response to chat (styled in yellow)
      const hintResponseMessage = {
        id: `hint-ai-${Date.now()}`,
        type: 'ai',
        content: hintText,
        timestamp: new Date().toISOString(),
        isHint: true, // Mark as hint for yellow styling
        evaluationStatus: 'hint'
      };
      
      const updatedMessages = [...chatMessages, hintRequestMessage, hintResponseMessage];
      setChatMessages(updatedMessages);
      
      // Save hint conversation to localStorage
      const hintAttemptKey = `hld-attempt-${problemId}`;
      const hintAttemptData = JSON.parse(localStorage.getItem(hintAttemptKey) || '{}');
      hintAttemptData.nonFunctionalRequirementsConversation = updatedMessages;
      hintAttemptData.timestamp = Date.now();
      localStorage.setItem(hintAttemptKey, JSON.stringify(hintAttemptData));
      
      // Save to backend fire-and-forget so loading clears immediately
      saveConversationToBackend(updatedMessages);
    } catch (error) {
      const errorMessage = {
        id: `hint-error-${Date.now()}`,
        type: 'ai',
        content: '⚠️ Sorry, I encountered an error while generating a hint. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const saveConversationToBackend = async (conversation) => {
    if (!problemId) return;
    
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    
    try {
      await postRequest(
        process.env.REACT_APP_URL + '/hld-attempt/update-metadata',
        {
          userId: userId,
          hldProblemId: problemId,
          nonFunctionalRequirementsConversation: conversation
        }
      );
    } catch (err) {
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box 
        className="feature-list-header"
        sx={{ 
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.13
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          color: '#333',
          fontSize: '1.1rem',
          lineHeight: 1.2
        }}>
          Non-Functional Requirements
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setDocumentVisible(!documentVisible)}
          startIcon={<Description />}
          size="small"
          sx={{
            borderColor: '#008080',
            color: '#008080',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#006666'
            }
          }}
        >
          {documentVisible ? 'Hide' : 'View Doc'}
        </Button>
      </Box>

      {/* Chat Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        width: '100%',
        p: 2,
        pb: 2,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8'
          }
        }
      }}>
        <List sx={{ p: 0, width: '100%' }}>
          {chatMessages.map((message) => (
            <ListItem 
              key={message.id}
              sx={{ 
                p: 0, 
                mb: 2,
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                width: '100%'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                gap: 0.5,
                maxWidth: '60%',
                width: 'fit-content',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
              }}>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: message.type === 'user' ? '#008080' : (message.isHint ? '#fff3cd' : '#f8f9fa'),
                  color: message.type === 'user' ? 'white' : (message.isHint ? '#856404' : 'text.primary'),
                  borderRadius: 2,
                  maxWidth: '100%',
                  width: 'fit-content',
                  minWidth: '80px',
                  border: message.type === 'user' ? 'none' : (message.isHint ? '1px solid #ffeaa7' : '1px solid #e0e0e0'),
                  boxShadow: message.isHint ? '0 2px 8px rgba(255,193,7,0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <Typography variant="body2" sx={{ 
                    wordBreak: 'break-word', 
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: 1.5
                  }}>
                    {message.content}
                  </Typography>
                  {message.evaluation && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={message.evaluation.status || 'CORRECT'}
                        size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          bgcolor: message.evaluation.status === 'CORRECT' ? '#e8f5e9' : 
                                   message.evaluation.status === 'REPEATED' ? '#fff3e0' : '#ffebee',
                          color: message.evaluation.status === 'CORRECT' ? '#2e7d32' : 
                                 message.evaluation.status === 'REPEATED' ? '#e65100' : '#c62828',
                          fontWeight: 'bold'
                        }}
                      />
                      {message.evaluation.evaluation_details && (
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '12px', mt: 0.5 }}>
                          {message.evaluation.evaluation_details.reasoning || message.evaluation.evaluation_details.suggestions}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            </ListItem>
          ))}
          
          {(isSending || isValidating) && (
            <ListItem sx={{ p: 0, mb: 2, justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#008080' }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                  Processing...
                </Typography>
              </Box>
            </ListItem>
          )}
          
          <div ref={chatEndRef} />
        </List>
      </Box>

      {/* Input Area */}
      <Box sx={{ 
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fafbfc',
        borderTop: '1px solid #f0f0f0',
        p: 1,
        flexShrink: 0,
        backdropFilter: 'blur(10px)',
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        zIndex: 10,
        mt: 'auto'
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 0.75, 
          alignItems: 'flex-end',
          backgroundColor: 'white',
          borderRadius: 3,
          p: 0.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0'
        }}>
          <SpeechToTextInput
            fullWidth
            multiline
            minRows={1}
            maxRows={8}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Write a non-functional requirement or user story..."
            variant="standard"
            size="small"
            disabled={isSending}
            onKeyPress={handleKeyPress}
            sx={{
              '& .MuiInput-root': {
                fontSize: '14px',
                minHeight: 'auto',
                '&:before': { border: 'none' },
                '&:after': { border: 'none' },
                '&:hover:before': { border: 'none' },
                '&:focus-within:before': { border: 'none' }
              },
              '& .MuiInput-input': {
                padding: '8px 12px',
                '&::placeholder': {
                  color: '#9ca3af',
                  opacity: 1
                }
              }
            }}
          />
          <Tooltip title="Get a hint">
            <Button
              variant="outlined"
              onClick={handleHint}
              disabled={isSending || isValidating}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 32,
                borderRadius: '50%',
                borderColor: '#f59e0b',
                color: '#f59e0b',
                flexShrink: 0,
                '&:hover': { 
                  borderColor: '#d97706',
                  backgroundColor: '#fef3c7',
                  transform: 'scale(1.05)'
                },
                '&:disabled': {
                  borderColor: '#e5e7eb',
                  color: '#d1d5db'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Lightbulb sx={{ fontSize: 14, margin: 'auto' }} />
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!userInput.trim() || isSending || isValidating}
            sx={{
              background: 'linear-gradient(135deg, #008080 0%, #006666 100%)',
              minWidth: 'auto',
              width: 32,
              height: 32,
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0,128,128,0.3)',
              flexShrink: 0,
              '&:hover': { 
                background: 'linear-gradient(135deg, #006666 0%, #004d4d 100%)',
                boxShadow: '0 4px 12px rgba(0,128,128,0.4)',
                transform: 'scale(1.05)'
              },
              '&:disabled': {
                background: '#e5e7eb',
                boxShadow: 'none'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {(isSending || isValidating) ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Send sx={{ color: 'white', fontSize: 14 }} />}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}